import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";
import { BriefcaseIcon as Briefcase, FilePdfIcon as FilePdf, FileTextIcon as FileText, ShieldCheckIcon as ShieldCheck, ScalesIcon as Scales, CurrencyDollarIcon as CurrencyDollar, DownloadSimpleIcon as DownloadSimple, ArrowSquareOutIcon as ArrowSquareOut, PrinterIcon as Printer, BankIcon as Bank, HandCoinsIcon as HandCoins, CheckCircleIcon as CheckCircle, CaretRightIcon as CaretRight, ListChecksIcon as ListChecks, BuildingsIcon as Buildings } from "@phosphor-icons/react";

/**
 * /admin/operator-binder — single bookmark-worthy hub for every document
 * the founder hands to a broker, bank, vendor, investor, or auditor.
 *
 * Aggregates documents that live in many places today:
 *   - Public legal pages (Terms, Privacy, Storefront Agreement, Refund)
 *   - Internal admin pages (Business Classification, Security, Legal Bundle)
 *   - Generated artifacts (Business Plan PDF, Insurance Bundle TXT/PDF)
 *
 * Designed to be the single page the founder opens before any external
 * upload. Includes per-audience checklists so it's clear which subset to
 * grab for an insurance form vs. a bank vs. a vendor security review.
 */

// Customer-facing legal pages (public URLs — each has a Print/Save PDF button)
const LEGAL_PAGES = [
  {
    title: "Terms of Service",
    href: "/terms",
    summary: "Master customer contract. Use this when a form asks for 'sample contract used with customers.'",
    primary: true,
  },
  {
    title: "Privacy Policy",
    href: "/privacy",
    summary: "Data handling, GDPR/CCPA notice. Attach alongside Terms for cyber & data-handling requirements.",
  },
  {
    title: "Storefront Seller Agreement",
    href: "/storefront-seller-agreement",
    summary: "Click-through contract every Dog Shop subscriber accepts. Establishes seller as merchant of record + 5% platform fee.",
  },
  {
    title: "Refund Policy",
    href: "/refund",
    summary: "Often requested on e-commerce / cyber applications.",
  },
  {
    title: "Cookie Notice",
    href: "/cookies",
    summary: "Cookie inventory for any GDPR / CCPA questionnaire.",
  },
  {
    title: "Sub-processors",
    href: "/sub-processors",
    summary: "List of third-party data processors (Stripe, Resend, Cloudflare, Daily.co, MongoDB Atlas, Emergent). Vendor security reviews ask for this.",
  },
  {
    title: "Security Measures",
    href: "/security",
    summary: "Public-facing security overview. Hand to enterprise customers asking about your infosec posture.",
  },
  {
    title: "Photo & Content License",
    href: "/content-license",
    summary: "Covers user-uploaded photos + The Dog's Voice content. Often needed on media-liability sections.",
  },
  {
    title: "Auto-renewal Disclosure",
    href: "/auto-renew",
    summary: "Required disclosure for subscription billing in CA, NY, IL, OR.",
  },
  {
    title: "Accessibility Statement",
    href: "/accessibility",
    summary: "ADA / WCAG posture. Some vendor security questionnaires ask.",
  },
  {
    title: "AI Use Disclosure",
    href: "/ai-use",
    summary: "How AI is used on the platform. Helpful for SOC2-lite questionnaires.",
  },
  {
    title: "Ethical Considerations",
    href: "/ethics",
    summary: "Animal welfare posture, breed-specific guardrails. Useful for partner / sponsor onboarding.",
  },
  {
    title: "Compliance Disclosures",
    href: "/compliance",
    summary: "State-by-state compliance disclosures. Useful for vendor reviews.",
  },
];

const ADMIN_DOCS = [
  {
    title: "Business & Insurance Classification",
    href: "/admin/business-classification",
    icon: Briefcase,
    summary: "The one-sentence business description, NAICS codes, what insurance coverage to ask for, and the full Insurance Application Answer Key.",
    accent: "#8A6A00",
    flag: "Start here for any insurance form",
  },
  {
    title: "Security & Compliance",
    href: "/admin/security",
    icon: ShieldCheck,
    summary: "Read-only inventory of every security control. Hand-printable for underwriters and enterprise security reviews.",
    accent: "#0E0E0E",
  },
  {
    title: "Launch Pack — Business Plan + Bundle",
    href: "/admin/launch-pack",
    icon: FilePdf,
    summary: "One-click downloads: Business Plan PDF (the comprehensive doc), Insurance & Legal Bundle (auto-generated dossier), SEO inventory, sample tour URL.",
    accent: "#C9A227",
    flag: "Business Plan PDF lives here",
  },
  {
    title: "Legal Bundle (CMS)",
    href: "/admin/legal-bundle",
    icon: Scales,
    summary: "Live editor for all public legal pages. Republish without a code deploy. Import lawyer's redlines from Word / PDF.",
    accent: "#0F8043",
  },
  {
    title: "Pricing Console",
    href: "/admin/pricing",
    icon: CurrencyDollar,
    summary: "Source-of-truth for every SKU, subscription, and platform fee. Spot-check before any application asks for fee tables.",
    accent: "#C1272D",
  },
];

const AUDIENCE_CHECKLISTS = [
  {
    audience: "Insurance application (Tech E&O / Cyber / GL)",
    icon: ShieldCheck,
    accent: "#0E0E0E",
    items: [
      { label: "Business & Insurance Classification (NAICS + answer key)", href: "/admin/business-classification" },
      { label: "Terms of Service (the 'sample customer contract')", href: "/terms", external: true },
      { label: "Privacy Policy", href: "/privacy", external: true },
      { label: "Security & Compliance inventory", href: "/admin/security" },
      { label: "Business Plan PDF (Compliance & Risk Posture section)", href: "/admin/launch-pack" },
      { label: "Auto-generated Insurance & Legal Bundle (download from Launch Pack)", href: "/admin/launch-pack" },
    ],
  },
  {
    audience: "Bank / merchant account application",
    icon: Bank,
    accent: "#0F8043",
    items: [
      { label: "Business Plan PDF (revenue model + KYC story)", href: "/admin/launch-pack" },
      { label: "Terms of Service", href: "/terms", external: true },
      { label: "Refund Policy", href: "/refund", external: true },
      { label: "Sub-processors (shows Stripe as merchant of record)", href: "/sub-processors", external: true },
      { label: "Pricing Console (clean fee tables)", href: "/admin/pricing" },
    ],
  },
  {
    audience: "Vendor / enterprise customer security review",
    icon: Buildings,
    accent: "#C9A227",
    items: [
      { label: "Security Measures (public)", href: "/security", external: true },
      { label: "Security & Compliance (admin inventory)", href: "/admin/security" },
      { label: "Privacy Policy", href: "/privacy", external: true },
      { label: "Sub-processors", href: "/sub-processors", external: true },
      { label: "AI Use Disclosure", href: "/ai-use", external: true },
      { label: "Accessibility Statement", href: "/accessibility", external: true },
    ],
  },
  {
    audience: "Investor / pitch dossier",
    icon: HandCoins,
    accent: "#C1272D",
    items: [
      { label: "Business Plan PDF (full)", href: "/admin/launch-pack" },
      { label: "Pricing Console (current revenue surfaces)", href: "/admin/pricing" },
      { label: "Business & Insurance Classification (legal posture)", href: "/admin/business-classification" },
    ],
  },
];

export default function AdminOperatorBinder() {
  const [bpMeta, setBpMeta] = useState(null);
  const [downloadingBp, setDownloadingBp] = useState(false);
  const [downloadingBundle, setDownloadingBundle] = useState(false);

  useEffect(() => {
    api
      .get("/admin/launch-pack/business-plan")
      .then((r) => setBpMeta(r.data))
      .catch(() => setBpMeta(null));
  }, []);

  const downloadFile = async (path, fallbackName, setter) => {
    setter(true);
    try {
      const r = await api.get(path, { responseType: "blob" });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      const cd = r.headers["content-disposition"] || "";
      const m = cd.match(/filename="([^"]+)"/);
      a.download = m ? m[1] : fallbackName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Downloaded.");
    } catch (e) {
      toast.error("Couldn't download — try the page directly.");
    } finally {
      setter(false);
    }
  };

  return (
    <AppShell>
      <div
        className="max-w-6xl mx-auto px-6 py-8 print:py-2"
        data-testid="admin-operator-binder"
      >
        {/* Header */}
        <div className="mb-8 flex items-start justify-between flex-wrap gap-4">
          <div>
            <Link
              to="/admin"
              className="text-[12px] text-ink/55 hover:text-ink inline-flex items-center gap-1"
            >
              ← Admin Console
            </Link>
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mt-3 mb-1">
              Operations · External-facing documents
            </p>
            <h1 className="font-spectral font-light text-4xl md:text-5xl tracking-tight text-ink">
              Operator Binder
            </h1>
            <p className="text-ink/65 mt-3 text-base leading-relaxed max-w-3xl">
              The single place to find every document you might hand to a
              broker, bank, vendor, auditor, or investor. Bookmark this page.
              The checklists at the bottom tell you exactly which subset to
              grab for each kind of submission.
            </p>
          </div>
        </div>

        {/* Quick downloads */}
        <section className="mb-10" data-testid="binder-quick-downloads">
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-3">
            Quick downloads
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="rounded-2xl border-2 border-[#C9A227] bg-gradient-to-br from-[#FBF7EC] to-white p-5 flex items-start gap-4">
              <span
                className="h-12 w-12 rounded-xl flex items-center justify-center shrink-0 bg-[#C9A227]/10 text-[#8A6A00]"
              >
                <FilePdf size={22} weight="duotone" />
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-2">
                  <h3 className="font-spectral text-lg tracking-tight text-ink">
                    Business Plan PDF
                  </h3>
                  {bpMeta && !bpMeta.missing && (
                    <span className="text-[10px] font-tabular text-ink/45">
                      {(bpMeta.size_bytes / 1024).toFixed(0)} KB
                    </span>
                  )}
                </div>
                <p className="text-[12.5px] text-ink/65 leading-snug mt-1 mb-3">
                  Comprehensive operating doc with the new Compliance &amp; Risk
                  Posture section. Multi-audience.
                </p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    className="rounded-full h-8 text-[11.5px] bg-ink hover:bg-crimson text-white"
                    disabled={downloadingBp}
                    onClick={() =>
                      downloadFile(
                        "/admin/launch-pack/business-plan/download.pdf",
                        "armstrong-business-plan.pdf",
                        setDownloadingBp,
                      )
                    }
                    data-testid="binder-download-bp-pdf"
                  >
                    <DownloadSimple size={12} weight="bold" className="mr-1" />
                    {downloadingBp ? "Downloading…" : "Download PDF"}
                  </Button>
                  <Link to="/admin/launch-pack">
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-full h-8 text-[11.5px] border-stone-300"
                    >
                      Open Launch Pack
                    </Button>
                  </Link>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border-2 border-stone-200 bg-white p-5 flex items-start gap-4">
              <span className="h-12 w-12 rounded-xl flex items-center justify-center shrink-0 bg-stone-100 text-ink">
                <FileText size={22} weight="duotone" />
              </span>
              <div className="flex-1 min-w-0">
                <h3 className="font-spectral text-lg tracking-tight text-ink">
                  Insurance &amp; Legal Bundle
                </h3>
                <p className="text-[12.5px] text-ink/65 leading-snug mt-1 mb-3">
                  Auto-generated dossier from the live platform — consents,
                  protections, integrations, security controls. Reads like
                  an underwriter briefing.
                </p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    className="rounded-full h-8 text-[11.5px] bg-ink hover:bg-crimson text-white"
                    disabled={downloadingBundle}
                    onClick={() =>
                      downloadFile(
                        "/admin/launch-pack/insurance-bundle/download.pdf",
                        "armstrong-insurance-bundle.pdf",
                        setDownloadingBundle,
                      )
                    }
                    data-testid="binder-download-bundle-pdf"
                  >
                    <DownloadSimple size={12} weight="bold" className="mr-1" />
                    {downloadingBundle ? "Downloading…" : "Download PDF"}
                  </Button>
                  <Link to="/admin/launch-pack">
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-full h-8 text-[11.5px] border-stone-300"
                    >
                      Preview &amp; share
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Admin reference docs */}
        <section className="mb-10" data-testid="binder-admin-docs">
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-3">
            Admin reference pages
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {ADMIN_DOCS.map((d) => {
              const Icon = d.icon;
              return (
                <Link
                  key={d.href}
                  to={d.href}
                  className="rounded-xl border border-stone-200 bg-white p-4 hover:border-gold hover:shadow-sm transition-all flex items-start gap-3"
                  data-testid={`binder-link-${d.href.replace(/\//g, "-")}`}
                >
                  <span
                    className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0"
                    style={{ background: `${d.accent}15`, color: d.accent }}
                  >
                    <Icon size={18} weight="duotone" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2 mb-1">
                      <h3 className="font-semibold text-ink text-[14px]">
                        {d.title}
                      </h3>
                      <CaretRight size={14} className="text-ink/30 shrink-0" />
                    </div>
                    <p className="text-[12px] text-ink/65 leading-snug">
                      {d.summary}
                    </p>
                    {d.flag && (
                      <span className="inline-flex items-center gap-1 mt-2 px-2 py-0.5 rounded-full bg-gold/10 text-[10px] font-bold uppercase tracking-wider text-[#8A6A00] border border-gold/30">
                        <CheckCircle size={10} weight="fill" />
                        {d.flag}
                      </span>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        </section>

        {/* Customer-facing legal pages */}
        <section className="mb-10" data-testid="binder-legal-pages">
          <div className="flex items-baseline justify-between gap-3 mb-3 flex-wrap">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-1">
                Customer-facing legal pages
              </p>
              <h2 className="font-spectral font-light text-2xl tracking-tight text-ink">
                Open and print as PDF
              </h2>
            </div>
            <p className="text-[11.5px] text-ink/55 max-w-md text-right italic">
              Each page has a <Printer size={11} weight="duotone" className="inline mb-0.5" /> Print / Save PDF button in the top-right. The PDF strips the navigation and footer.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2.5">
            {LEGAL_PAGES.map((p) => {
              const slug = p.href.replace(/^\//, "");
              return (
                <div
                  key={p.href}
                  className={`rounded-xl border bg-white p-3.5 hover:shadow-sm transition-all flex flex-col gap-1.5 ${
                    p.primary
                      ? "border-2 border-emerald-300 hover:border-emerald-500"
                      : "border-stone-200 hover:border-gold"
                  }`}
                  data-testid={`binder-legal-${p.href.replace(/\//g, "-")}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span className="font-semibold text-ink text-[13px] truncate">
                        {p.title}
                      </span>
                      {p.primary && (
                        <span className="text-[9px] uppercase tracking-wider font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-0.5 shrink-0">
                          Primary
                        </span>
                      )}
                    </div>
                  </div>
                  <p className="text-[11.5px] text-ink/60 leading-snug">{p.summary}</p>
                  <div className="flex items-center gap-2 mt-1.5">
                    <a
                      href={`/api/public/legal/${slug}/download.pdf`}
                      download
                      className="inline-flex items-center gap-1 text-[10.5px] font-bold text-white bg-ink hover:bg-crimson rounded-full px-2.5 py-1 transition-colors"
                      data-testid={`binder-legal-pdf-${slug}`}
                    >
                      <DownloadSimple size={11} weight="bold" /> PDF
                    </a>
                    <a
                      href={p.href}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-[10.5px] text-ink/65 hover:text-ink border border-stone-300 rounded-full px-2.5 py-1 transition-colors"
                    >
                      <ArrowSquareOut size={10} /> View
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Audience checklists */}
        <section className="mb-12" data-testid="binder-checklists">
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-1">
            Audience checklists
          </p>
          <h2 className="font-spectral font-light text-2xl tracking-tight text-ink mb-2">
            Which documents to grab for what
          </h2>
          <p className="text-[13px] text-ink/65 mb-5 max-w-3xl leading-relaxed">
            Each list below tells you the exact subset to attach for that
            kind of submission. Open all the links, click Print / Save PDF
            on each, attach the PDFs.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {AUDIENCE_CHECKLISTS.map((c) => {
              const Icon = c.icon;
              return (
                <div
                  key={c.audience}
                  className="rounded-2xl border border-stone-200 bg-white overflow-hidden"
                  data-testid={`binder-checklist-${c.audience.toLowerCase().replace(/[^a-z]/g, "-").slice(0, 30)}`}
                >
                  <div
                    className="px-5 py-3 border-b border-stone-200 flex items-center gap-3"
                    style={{ background: `${c.accent}08` }}
                  >
                    <span
                      className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0"
                      style={{ background: `${c.accent}15`, color: c.accent }}
                    >
                      <Icon size={16} weight="duotone" />
                    </span>
                    <h3 className="font-spectral text-base tracking-tight text-ink">
                      {c.audience}
                    </h3>
                  </div>
                  <ul className="divide-y divide-stone-100">
                    {c.items.map((item, i) => (
                      <li key={i}>
                        {item.external ? (
                          <a
                            href={item.href}
                            target="_blank"
                            rel="noreferrer"
                            className="block px-5 py-2.5 hover:bg-stone-50 transition-colors text-[13px] text-ink/85 flex items-center justify-between gap-2"
                          >
                            <span>{item.label}</span>
                            <ArrowSquareOut size={12} className="text-ink/40 shrink-0" />
                          </a>
                        ) : (
                          <Link
                            to={item.href}
                            className="block px-5 py-2.5 hover:bg-stone-50 transition-colors text-[13px] text-ink/85 flex items-center justify-between gap-2"
                          >
                            <span>{item.label}</span>
                            <CaretRight size={12} className="text-ink/40 shrink-0" />
                          </Link>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </section>

        {/* How to PDF anything */}
        <section className="mb-16">
          <div className="rounded-2xl bg-stone-50 border border-stone-200 p-5 flex items-start gap-4">
            <ListChecks size={20} weight="duotone" className="text-ink/55 shrink-0 mt-0.5" />
            <div>
              <h3 className="font-spectral text-lg tracking-tight text-ink mb-2">
                How to turn any page in this binder into a PDF
              </h3>
              <ol className="text-[12.5px] text-ink/70 leading-relaxed list-decimal pl-5 space-y-1">
                <li>Open the page (click any link above).</li>
                <li>
                  Use the <Printer size={11} weight="duotone" className="inline mb-0.5" /> Print / Save PDF button if the page has one (legal pages, admin reference pages), or hit ⌘P / Ctrl+P.
                </li>
                <li>In the print dialog, choose <strong>Save as PDF</strong> as the destination.</li>
                <li>Attach the PDF wherever it&rsquo;s needed.</li>
              </ol>
              <p className="text-[11.5px] text-ink/55 italic mt-3">
                The Business Plan and Insurance Bundle are pre-rendered as
                proper PDFs by the backend (the &ldquo;Download PDF&rdquo;
                buttons above) — no print dialog needed.
              </p>
            </div>
          </div>
        </section>
      </div>
    </AppShell>
  );
}
